/**
 * Home's Findings tab: open findings by document, the first document open,
 * the first three documents shown and the rest under "N more".
 *
 * @module components/home/HomeFindingGroups
 */
import { ChevronDownIcon } from "lucide-react";
import { useState } from "react";

import { cn } from "~/lib/utils";
import { TIMELINE_DISCLOSURE_PANEL_CLASS } from "../chat/timelineDisclosure";
import { Collapsible, CollapsiblePanel } from "../ui/collapsible";
import { ListRowIcon } from "../ui/list-row";
import type { HomeFindingGroup as FindingGroup } from "./HomeFindings.logic";
import { HomeFindingGroup } from "./HomeFindingGroup";
import { HomeListEmpty } from "./HomeListEmpty";
import { FINDING_GROUPS_SHOWN } from "./homeTabs.logic";

export function HomeFindingGroups({
  groups,
  orgSegment,
  now,
}: {
  readonly groups: ReadonlyArray<FindingGroup>;
  readonly orgSegment: string;
  readonly now: number;
}) {
  const [showAll, setShowAll] = useState(false);
  if (groups.length === 0) return <HomeListEmpty>No open findings</HomeListEmpty>;
  return (
    <div className="flex flex-col">
      {groups.slice(0, FINDING_GROUPS_SHOWN).map((group, index) => (
        <HomeFindingGroup key={group.key} group={group} orgSegment={orgSegment} now={now} defaultOpen={index === 0} />
      ))}
      <Collapsible open={showAll} onOpenChange={setShowAll}>
        <CollapsiblePanel className={TIMELINE_DISCLOSURE_PANEL_CLASS}>
          {groups.slice(FINDING_GROUPS_SHOWN).map((group) => (
            <HomeFindingGroup key={group.key} group={group} orgSegment={orgSegment} now={now} defaultOpen={false} />
          ))}
        </CollapsiblePanel>
      </Collapsible>
      {groups.length > FINDING_GROUPS_SHOWN ? (
        <button
          type="button"
          onClick={() => setShowAll((value) => !value)}
          className="mt-0.5 flex min-h-8 items-center gap-2 rounded-md px-2 text-xs text-muted-foreground outline-none transition-colors hover:bg-accent/70 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ListRowIcon>
            <ChevronDownIcon className={cn("transition-transform", showAll && "rotate-180")} />
          </ListRowIcon>
          {showAll ? "Show fewer" : `${groups.length - FINDING_GROUPS_SHOWN} more`}
        </button>
      ) : null}
    </div>
  );
}
